import React, { useLayoutEffect, useState, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputWithActionButton from "../Advisors/advisorAuth/InputWithActionButton";
import CustomSelect from "../../components/common/CustomSelect";
import { CloudUpload, Trash2, AlertTriangle } from "lucide-react";
import CustomModal from "../../components/common/CustomDialog/CustomModal";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../Redux Store/Slices/auth";
import { useNavigate } from "react-router-dom";
import AdvisorConditionsPage from "../common/AdvisorConditionsPage";
import axios from "axios";
import { baseUrl } from "../../utils/urls";
import { toast } from "react-toastify";

const deleteAccountUrl = baseUrl + "api/advisor/delete-advisor-account";

const ProfilePage = () => {
  const loggedInUser = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const formikRef = useRef(null);

  useLayoutEffect(() => {
    if (loggedInUser?.user?.advisor_id) {
      formikRef?.current?.setFieldValue("name", loggedInUser?.user?.name);
      formikRef?.current?.setFieldValue("qualification", loggedInUser?.user?.qualification);
      formikRef?.current?.setFieldValue("mobile_number", loggedInUser?.user?.mobile_number);
      formikRef?.current?.setFieldValue("email", loggedInUser?.user?.email);
      formikRef?.current?.setFieldValue("bank_ifsc_code", loggedInUser?.user?.bank_details?.[0]?.bank_ifsc_code);
      formikRef?.current?.setFieldValue("bank_name", loggedInUser?.user?.bank_details?.[0]?.bank_name);
      formikRef?.current?.setFieldValue("bank_branch", loggedInUser?.user?.bank_details?.[0]?.bank_ifsc_code);
      formikRef?.current?.setFieldValue("bank_account_number", loggedInUser?.user?.bank_details?.[0]?.bank_account_number);
    }
  }, []);

  const initialValues = {
    name: "",
    mobile_number: "",
    qualification: "",
    email: "",
    bank_ifsc_code: "",
    bank_name: "",
    bank_branch: "",
    bank_account_number: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    mobile: Yup.string().required("Mobile number is required"),
    qualification: Yup.string().required("Qualification is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    bank_ifsc_code: Yup.string().required("IFSC code is required"),
    bank_name: Yup.string().required("Bank name is required"),
    bank_branch: Yup.string().required("Bank branch is required"),
    bank_account_number: Yup.string().required("Account number is required"),
  });

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(deleteAccountUrl);
      toast.success("Account deleted successfully");
      dispatch(logout());
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete account. Please contact support.");
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="page-container flex-1">
      {loggedInUser?.isSkipLogin || !loggedInUser?.isAuthenticated ? (
        <AdvisorConditionsPage type="Skip/NotLogin" />
      ) : (
        <div className="flex flex-1 flex-col gap-8 items-start w-full">
          <AdvisorConditionsPage type="Status" />

          <div className="flex flex-1 flex-row gap-8 items-start w-full">
            <div className="flex-1">
              <div className="flex flex-1 flex-col gap-8 items-start w-full mb-6">
                <h1 className="font-bold text-md">
                  Lead ID : {loggedInUser?.user?.advisor_display_id || "-"}
                </h1>
              </div>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => console.log("Form submitted:", values)}
                innerRef={formikRef}
              >
                <Form className="grid grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <h1 className="font-bold text-md">Basic Details :</h1>
                  </div>
                  <InputWithActionButton name="name" label="Full Name" placeholder="Enter your name" required disabled />
                  <InputWithActionButton name="mobile_number" label="Mobile Number" placeholder="Enter your mobile number" required disabled />
                  <CustomSelect
                    required
                    name="qualification"
                    label="Qualification"
                    placeholder="Select qualification"
                    menuList={[
                      { label: "10th", value: "10th" },
                      { label: "12th", value: "12th" },
                      { label: "Graduate", value: "Graduate" },
                      { label: "Post Graduate", value: "Post Graduate" },
                    ]}
                    disabled
                  />
                  <InputWithActionButton name="email" label="Email" placeholder="Enter your email" required disabled />
                  <div className="col-span-2">
                    <h1 className="font-bold text-md">Bank Details :</h1>
                  </div>
                  <InputWithActionButton name="bank_ifsc_code" label="Bank IFSC Code" required placeholder="Enter IFSC code" disabled />
                  <InputWithActionButton name="bank_name" label="Bank Name" required placeholder="Enter bank name" disabled />
                  <InputWithActionButton name="bank_branch" label="Bank Branch" required placeholder="Enter bank branch" disabled />
                  <InputWithActionButton name="bank_account_number" label="Account Number" required placeholder="Enter account number" disabled />
                </Form>
              </Formik>

              {/* Danger zone */}
              <div className="mt-10 border border-red-200 rounded-xl p-5 bg-red-50">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[0.9375rem] font-bold text-red-600">Danger Zone</p>
                    <p className="text-[0.8125rem] text-red-500 mt-0.5">
                      Deleting your account is permanent and cannot be undone. All your data, leads, and invoices will be permanently removed.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-red-300 text-red-600 text-[0.875rem] font-semibold rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 size={16} />
                  Delete My Account
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 size={22} className="text-red-500" />
              </div>
              <div>
                <p className="text-[1rem] font-bold text-[#0F172A]">Delete Account?</p>
                <p className="text-[0.8125rem] text-[#64748B]">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-[0.875rem] text-[#475569] mb-6">
              All your leads, invoices, and profile data will be permanently deleted. You will be logged out immediately.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 border border-[#E2E8F0] rounded-xl text-[0.875rem] font-semibold text-[#475569] hover:bg-[#F8FAFC] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-[0.875rem] font-semibold hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {deleteLoading ? "Deleting…" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile photo dialog */}
      {isDialogOpen && (
        <CustomModal
          title="Update Profile Photo"
          open={isDialogOpen}
          onCancel={(onClose) => { onClose(); setIsDialogOpen(false); }}
          onSubmit={(onClose) => { onClose(); setIsDialogOpen(false); }}
        >
          <div className="p-4">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) { file.preview = URL.createObjectURL(file); setProfileImage(file); }
              }}
              id="upload-profile-photo"
              className="hidden"
            />
            <label
              htmlFor="upload-profile-photo"
              className="border-2 border-dashed border-gray-300 w-full h-52 flex flex-col justify-center items-center gap-2 cursor-pointer hover:bg-blue-50 transition"
            >
              {profileImage ? (
                <img src={profileImage.preview} alt="Preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <>
                  <CloudUpload className="text-gray-300 w-8 h-8" />
                  <p className="text-gray-500 text-sm">Click or drag to upload profile photo</p>
                </>
              )}
            </label>
          </div>
        </CustomModal>
      )}
    </div>
  );
};

export default ProfilePage;
